// Dream Gallery — the images generated from dream entries.
//
// Generation itself lives in the composer (you imagine a specific dream, not a blank canvas), so this
// screen is the archive: browse, open, delete, and see what's left of your monthly allowance.

import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { canGenerate, deleteVision, fetchVisionUsage, fetchVisions, type Vision, type VisionUsage } from '../api/visions';
import { useEntries } from '../entries/EntriesProvider';
import { EmptyState, Spinner } from '../components/ui';
import { IconClose, IconLock, IconMoon, IconTrash } from '../components/Icons';

export function DreamsScreen() {
  const { entries } = useEntries();
  const [visions, setVisions] = useState<Vision[] | null>(null);
  const [usage, setUsage] = useState<VisionUsage | null>(null);
  const [zoomed, setZoomed] = useState<Vision | null>(null);

  useEffect(() => {
    const controller = new AbortController();
    fetchVisions(controller.signal)
      .then(setVisions)
      .catch(() => setVisions([]));
    fetchVisionUsage(controller.signal)
      .then(setUsage)
      .catch(() => setUsage(null));
    return () => controller.abort();
  }, []);

  async function onDelete(id: string) {
    setVisions((current) => current?.filter((v) => v.id !== id) ?? null);
    setZoomed(null);
    try {
      await deleteVision(id);
    } catch {
      fetchVisions().then(setVisions).catch(() => undefined);
    }
  }

  const dreamCount = entries.filter((entry) => entry.kind === 'dream').length;

  return (
    <>
      <div className="ohead">
        <div className="obrand">
          <span className="nm" style={{ fontSize: 22 }}>Dreams</span>
        </div>
        <div className="olock">
          <IconLock /> Private
        </div>
      </div>

      <div className="ojhead">
        <h1>
          What your dreams
          <br />
          <em className="oitalic">looked like.</em>
        </h1>
        {usage ? (
          <p className="ojsub">
            {usage.isPremium
              ? `${usage.remainingMonthly} left this month${usage.remainingDaily < usage.remainingMonthly ? ` · ${usage.remainingDaily} left today` : ''}`
              : usage.introAvailable
                ? 'You have one introductory vision to try.'
                : 'Dream Vision is a Premium feature.'}
          </p>
        ) : null}
      </div>

      {visions === null ? (
        <Spinner label="Opening your gallery…" />
      ) : visions.length === 0 ? (
        <EmptyState
          title="No visions yet."
          body={
            dreamCount === 0
              ? 'Record a dream first, then Oreia can turn it into an image — without ever sending your words off our servers as written.'
              : 'Open a dream entry and choose Imagine to see it as an image.'
          }
          action={
            <Link className="octa" to={dreamCount === 0 ? '/write' : '/journal'} style={{ textDecoration: 'none' }}>
              <IconMoon /> {dreamCount === 0 ? 'Record a dream' : 'Open your dreams'}
            </Link>
          }
        />
      ) : (
        <div className="ogallery">
          {visions.map((vision) => (
            <button
              key={vision.id}
              className="otile"
              onClick={() => setZoomed(vision)}
              aria-label={`Open vision from ${formatDate(vision.createdAt)}`}
            >
              <img src={vision.signedUrl} alt="" loading="lazy" />
              <span className="otile-date omono">{formatDate(vision.createdAt)}</span>
            </button>
          ))}
        </div>
      )}

      {usage && !canGenerate(usage) && visions && visions.length > 0 ? (
        <p className="otext-mute" style={{ fontSize: 13, lineHeight: 1.6, marginTop: 20 }}>
          {usage.isPremium
            ? `You've used this month's visions. More on ${usage.resetsAt ? formatDate(usage.resetsAt) : 'your renewal date'}.`
            : 'New Premium plans include up to 10 visions a month, within monthly fair use.'}
        </p>
      ) : null}

      {zoomed ? (
        <div className="olightbox" role="dialog" aria-modal="true" onClick={() => setZoomed(null)}>
          <img src={zoomed.signedUrl} alt="" onClick={(e) => e.stopPropagation()} />
          <div className="olightbox-bar" onClick={(e) => e.stopPropagation()}>
            <span className="omono">{formatDate(zoomed.createdAt)}</span>
            <div className="orow" style={{ gap: 8 }}>
              {zoomed.entryId ? (
                <Link className="ochip" to={`/write/${zoomed.entryId}`} style={{ textDecoration: 'none' }}>
                  Open the dream
                </Link>
              ) : null}
              <button className="ochip" onClick={() => onDelete(zoomed.id)}>
                <IconTrash style={{ width: 14, height: 14 }} /> Delete
              </button>
              <button className="ochip" onClick={() => setZoomed(null)} aria-label="Close">
                <IconClose style={{ width: 14, height: 14 }} />
              </button>
            </div>
          </div>
        </div>
      ) : null}

      <div style={{ height: 60 }} />
    </>
  );
}

function formatDate(value: string): string {
  const date = new Date(value);
  return Number.isNaN(date.getTime())
    ? value
    : date.toLocaleDateString(undefined, { month: 'short', day: 'numeric', year: 'numeric' });
}
